import React, { useRef, useState, useEffect } from "react";
import {
  Tabs,
  FloatingIndicator,
  type TabsProps,
  ActionIcon,
  Box,
} from "@mantine/core";
import styles from "@scss/custom-components/tabs.module.scss";
import { hexToRgb } from "@/src/utils/color-utils";

type TabSize = "xs" | "sm" | "md" | "lg" | "xl";

interface CustomPillTabsProps extends TabsProps {
  tabs: string[];
  panels: React.ReactNode[];
  size?: TabSize;
  tabGrow?: boolean;
  centerTabs?: boolean;
  minTabWidth?: string;
  tabColors?: { [key: string]: string };
}

export function CustomPillTabs({
  tabs,
  panels,
  size = "md",
  tabGrow = true,
  centerTabs = false,
  minTabWidth = "120px",
  value,
  onChange,
  tabColors,
  ...props
}: CustomPillTabsProps) {
  const [internalActiveTab, setInternalActiveTab] = useState<string | null>(
    tabs[0] || null,
  );
  const [mounted, setMounted] = useState(false);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Use controlled value if provided, otherwise use internal state
  const activeTab = value !== undefined ? value : internalActiveTab;
  const handleTabChange = (newValue: string | null) => {
    if (onChange) {
      onChange(newValue);
    } else {
      setInternalActiveTab(newValue);
    }
  };
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const setTabRef = (value: string) => (node: HTMLButtonElement | null) => {
    tabRefs.current[value] = node;
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } =
        scrollContainerRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  useEffect(() => {
    // Wait until refs are mounted
    setMounted(true);
  }, []);

  useEffect(() => {
    checkScrollButtons();
    const handleResize = () => checkScrollButtons();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [tabs]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", checkScrollButtons);
      return () =>
        scrollContainer.removeEventListener("scroll", checkScrollButtons);
    }
  }, [mounted]);

  const showScrollArrows = showLeftArrow || showRightArrow;

  return (
    <Tabs
      value={activeTab}
      onChange={handleTabChange}
      variant="green"
      {...props}
      classNames={{
        root: styles.pillTabs,
        list: styles.list,
        tab: styles.tab,
      }}
    >
      <Box className={styles.tabsWrapper}>
        {showScrollArrows && (
          <ActionIcon
            variant="subtle"
            className={styles.scrollArrow}
            onClick={scrollLeft}
            size="sm"
            disabled={!showLeftArrow}
          >
            <></>
          </ActionIcon>
        )}

        <Box className={styles.scrollContainer} ref={scrollContainerRef}>
          <Tabs.List
            ref={rootRef}
            grow={tabGrow}
            className={`${centerTabs ? styles.listCenter : ""} ${
              tabGrow ? styles.listGrow : styles.listCompact
            } ${styles.scrollableList}`}
          >
            {tabs.map((tab) => {
              const tabColor = tabColors?.[tab];
              const tabStyle: React.CSSProperties = {
                minWidth: minTabWidth,
                ...(tabColor &&
                  ({
                    "--tab-color": tabColor,
                    "--tab-color-rgb": hexToRgb(tabColor),
                  } as React.CSSProperties)),
              };

              return (
                <Tabs.Tab
                  key={tab}
                  value={tab}
                  ref={setTabRef(tab)}
                  className={`${styles.tab} ${
                    size ? styles[`tabSize--${size}`] : ""
                  }`}
                  style={tabStyle}
                >
                  {tab}
                </Tabs.Tab>
              );
            })}

            {mounted &&
              tabRefs.current[activeTab ?? ""] &&
              (() => {
                const indicatorColor = tabColors?.[activeTab!];
                const indicatorStyle: React.CSSProperties = indicatorColor
                  ? ({
                      "--tab-color": indicatorColor,
                      "--tab-color-rgb": hexToRgb(indicatorColor),
                    } as React.CSSProperties)
                  : {};

                return (
                  <FloatingIndicator
                    target={tabRefs.current[activeTab!]}
                    parent={rootRef.current}
                    className={styles.pillFloatingIndicator}
                    style={indicatorStyle}
                  />
                );
              })()}
          </Tabs.List>
        </Box>

        {showScrollArrows && (
          <ActionIcon
            variant="subtle"
            className={styles.scrollArrow}
            onClick={scrollRight}
            size="sm"
            disabled={!showRightArrow}
          >
            <></>
          </ActionIcon>
        )}
      </Box>

      {tabs.map((tab, index) => (
        <Tabs.Panel key={tab} value={tab}>
          {panels[index]}
        </Tabs.Panel>
      ))}
    </Tabs>
  );
}
